import React, { useState, useEffect } from "react";
import "../CSS/ApiBuilderForm.css";
import ProjectService from "../Services/ProjectService";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { useParams } from "react-router-dom";
import axios from "axios";

const methodOptions = ["GET", "POST", "PUT", "DELETE", "PATCH"];
const fieldTypeOptions = {
  Common: ["string", "number", "boolean", "object", "array"],

  "Java – Primitive": [
    "byte",
    "short",
    "int",
    "long",
    "float",
    "double",
    "char",
  ],

  "Java – Wrapper": [
    "Byte",
    "Short",
    "Integer",
    "Long",
    "Float",
    "Double",
    "Boolean",
    "Character",
  ],

  "Java – Classes": [
    "String",
    "BigDecimal",
    "BigInteger",
    "Date",
    "LocalDate",
    "LocalDateTime",
    "UUID",
    "List",
    "Set",
    "Map",
    "Object",
  ],

  "Python – Built-in": [
    "int",
    "float",
    "bool",
    "str",
    "bytes",
    "bytearray",
    "None",
  ],

  "Python – Collections": ["list", "tuple", "set", "dict"],

  "Python – Date / Special": [
    "datetime",
    "date",
    "time",
    "Decimal",
    "UUID",
    "object",
  ],
};
const paramInOptions = ["query", "path", "header"];

const endpointSuggestions = ["/books", "/users", "/orders", "/products"];
const paramNameSuggestions = [
  "id",
  "title",
  "author",
  "category",
  "price",
  "userId",
];
const headerNameSuggestions = [
  "Content-Type",
  "Authorization",
  "Accept",
  "X-API-KEY",
];
const headerValueSuggestions = [
  "application/json",
  "Bearer {token}",
  "application/xml",
];
const schemaNameSuggestions = ["Book", "User", "Order", "Product"];

// Right after your imports and constants, before ApiBuilderForm
const FieldInput = ({ field, indexPath, onUpdate, onRemove, onAddChild }) => {
  const indentation = indexPath.length * 20;

  return (
    <div className="field-container" style={{ marginLeft: indentation }}>
      <label>Name:</label>
      <input
        type="text"
        value={field.name}
        onChange={(e) => onUpdate(indexPath, "name", e.target.value)}
        required
      />

      <label>Type:</label>
      <select
        value={field.type}
        onChange={(e) => onUpdate(indexPath, "type", e.target.value)}
        required
      >
        {Object.entries(fieldTypeOptions).map(([group, types]) => (
          <optgroup key={group} label={group}>
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <label>Example:</label>
      <textarea
        value={field.example ? JSON.stringify(field.example, null, 2) : ""}
        onChange={(e) => {
          const text = e.target.value;

          try {
            // Parse to real object
            const parsed = JSON.parse(text);
            onUpdate(indexPath, "example", parsed);
          } catch (err) {
            // Keep raw text until valid JSON
            onUpdate(indexPath, "example", text);
          }
        }}
        rows={4}
      />

      <label>Required:</label>
      <input
        type="checkbox"
        checked={field.required || false}
        onChange={(e) => onUpdate(indexPath, "required", e.target.checked)}
      />

      <button type="button" onClick={() => onRemove(indexPath)}>
        Remove
      </button>

      {["object", "array"].includes(field.type) && (
        <button type="button" onClick={() => onAddChild(indexPath)}>
          Add Child
        </button>
      )}

      {Array.isArray(field.children) &&
        field.children.map((child, i) => (
          <FieldInput
            key={i}
            field={child}
            indexPath={[...indexPath, i]}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onAddChild={onAddChild}
          />
        ))}
    </div>
  );
};

const ApiBuilderForm = ({
  initialData,
  onSubmit,
  projectId,
  projectBasePath,
  onCancel,
}) => {
  const [apiName, setApiName] = useState("");

  const [relativePath, setRelativePath] = useState(
    initialData?.relativePath || "",
  );
  const [method, setMethod] = useState(initialData?.method || "GET");
  const [summary, setSummary] = useState(initialData?.summary || "");
  const [moduleName, setModuleName] = useState(initialData?.moduleName || "");
  const [schemaName, setSchemaName] = useState(initialData?.schemaName || "");
  const [fields, setFields] = useState(initialData?.fields || []);
  const [params, setParams] = useState(initialData?.params || []);
  const [headers, setHeaders] = useState(initialData?.headers || []);
  const [auth, setAuth] = useState(
    initialData?.auth || {
      type: "none",
      username: "",
      password: "",
      token: "",
      apiKeyName: "",
      apiKeyValue: "",
    },
  );
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || "");
  const [basePath, setBasePath] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [activeSection, setActiveSection] = useState("parameters");
  const [showPathParams, setShowPathParams] = useState(
    relativePath.includes("{"),
  );
  const [moduleNameSuggestions, setModuleNameSuggestions] = useState([]);

  const { organizationId } = useParams();
  const [queryValues, setQueryValues] = useState({});

  const [isSchemaManuallyEdited, setIsSchemaManuallyEdited] = useState(false);

  // Always use raw JSON
  const [rawJson, setRawJson] = useState(initialData?.rawJson || "");

  const [bodyType, setBodyType] = useState("NONE");
  const [formFields, setFormFields] = useState([]);

  const [bodyTab, setBodyTab] = useState("raw");
  const [binaryFile, setBinaryFile] = useState(null);

  const [paramTab, setParamTab] = useState("query");

  const [isApiNameManuallyEdited, setIsApiNameManuallyEdited] = useState(false);

  const [formEditMode, setFormEditMode] = useState("KEY_VALUE");

  const [bulkText, setBulkText] = useState("");

  const [selectedModule, setSelectedModule] = useState(null);

  const DragDropZone = ({ onFilesSelected }) => {
    const handleDrop = (e) => {
      e.preventDefault();
      onFilesSelected(Array.from(e.dataTransfer.files));
    };

    return (
      <div
        className="drag-drop-zone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        Drag & Drop Files Here
      </div>
    );
  };

  const [localBasePath, setLocalBasePath] = useState(
    initialData?.localBasePath || projectBasePath || "",
  );

  const [serverBasePath, setServerBasePath] = useState(
    initialData?.serverBasePath || projectBasePath || "",
  );
  const [projectLocalBasePath, setProjectLocalBasePath] = useState("");
  const [projectServerBasePath, setProjectServerBasePath] = useState("");
  const [projectStageBasePath, setProjectStageBasePath] = useState("");

  const deriveSchemaNameFromApiName = (apiName) => {
    if (!apiName) return "";

    // Remove common verbs
    const verbs = ["Get", "Create", "Update", "Delete", "Patch"];
    let name = apiName;

    verbs.forEach((v) => {
      if (name.startsWith(v)) {
        name = name.replace(v, "");
      }
    });

    // Remove trailing ById / BySomething
    name = name.replace(/By[A-Z].*$/, "");

    // Ensure PascalCase
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  useEffect(() => {
    if (initialData?.apiName) {
      setApiName(initialData.apiName);
      setIsApiNameManuallyEdited(true); // prevent auto-override
    }
  }, [initialData]);

  useEffect(() => {
    if (formEditMode === "BULK") {
      setFormFields((prev) =>
        prev.map((f) => ({
          ...f,
          fieldType: "text",
          filePath: "",
          fileName: "",
        })),
      );
    }
  }, [formEditMode]);

  useEffect(() => {
    if (formEditMode === "BULK") {
      setBulkText(formFieldsToBulkText());
    }
  }, [formEditMode]);

  useEffect(() => {
    if (formEditMode === "KEY_VALUE") {
      setFormFields(bulkTextToFormFields(bulkText));
    }
  }, [formEditMode]);

  useEffect(() => {
    if (bodyType !== "FORM_DATA" && bodyType !== "X_WWW_FORM_URLENCODED") {
      setFormEditMode("KEY_VALUE");
    }
  }, [bodyType]);

  useEffect(() => {
    if (!isSchemaManuallyEdited && apiName.trim()) {
      const derivedSchema = deriveSchemaNameFromApiName(apiName.trim());

      if (derivedSchema && derivedSchema !== schemaName) {
        setSchemaName(derivedSchema);
      }
    }
  }, [apiName]);

  useEffect(() => {
    if (projectLocalBasePath || projectServerBasePath) {
      setLocalBasePath(projectLocalBasePath);
      setServerBasePath(projectServerBasePath);
    }
  }, [projectLocalBasePath, projectServerBasePath]);

  const convertJsonToFields = (jsonString) => {
    try {
      const json = JSON.parse(jsonString);

      const buildFields = (obj) => {
        return Object.keys(obj).map((key) => {
          const value = obj[key];

          const type = Array.isArray(value)
            ? "array"
            : typeof value === "object" && value !== null
              ? "object"
              : typeof value;

          const field = {
            name: key,
            type,
            example: type === "object" || type === "array" ? null : value,
            required: true,
            children: [],
          };

          if (type === "object") {
            field.children = buildFields(value);
          } else if (type === "array" && value.length > 0) {
            if (typeof value[0] === "object") {
              field.children = buildFields(value[0]);
            }
          }

          return field;
        });
      };

      return buildFields(json);
    } catch (err) {
      console.error("Invalid JSON for conversion:", err);
      return [];
    }
  };

  const detectTypeFromValue = (value) => {
    if (value === undefined || value === null || value === "") {
      return "string";
    }

    // Boolean detection
    if (value === "true" || value === "false") {
      return "boolean";
    }

    // Number detection
    if (!isNaN(value) && value.trim() !== "") {
      return "number";
    }

    return "string";
  };

  const buildFullUrlWithQuery = () => {
    const base = projectLocalBasePath || "";
    const pathOnly = relativePath.split("?")[0];

    const activeQueryParams = params.filter(
      (p) => p.paramIn === "query" && p.enabled !== false && p.name,
    );

    if (activeQueryParams.length === 0) {
      return base + pathOnly;
    }

    const queryString = activeQueryParams
      .map((p) => `${p.name}=${p.example ?? ""}`)
      .join("&");

    return `${base}${pathOnly}?${queryString}`;
  };

  const convertFieldsToJson = (fields) => {
    const buildJson = (fieldList) => {
      const obj = {};

      fieldList.forEach((f) => {
        if (f.type === "object") {
          obj[f.name] = buildJson(f.children || []);
        } else if (f.type === "array") {
          obj[f.name] =
            f.children && f.children.length > 0 ? [buildJson(f.children)] : [];
        } else {
          obj[f.name] = f.example ?? "";
        }
      });

      return obj;
    };

    return JSON.stringify(buildJson(fields), null, 2);
  };

  const showRequestBody = ["POST", "PUT", "PATCH"].includes(method);

  const deriveApiNameFromMethodAndPath = (method, path) => {
    if (!method || !path) return "";

    // Remove leading slash
    let cleanPath = path.replace(/^\//, "");

    // Split path
    const parts = cleanPath.split("/");

    const nameParts = parts.map((p) => {
      if (p.startsWith("{") && p.endsWith("}")) {
        const param = p.slice(1, -1);
        return "By" + param.charAt(0).toUpperCase() + param.slice(1);
      }
      return p.charAt(0).toUpperCase() + p.slice(1);
    });

    let verb = "Get";
    if (method === "POST") verb = "Create";
    if (method === "PUT") verb = "Update";
    if (method === "PATCH") verb = "Patch";
    if (method === "DELETE") verb = "Delete";

    return verb + nameParts.join("");
  };

  useEffect(() => {
    if (isApiNameManuallyEdited) return;

    if (method && relativePath) {
      const derivedApiName = deriveApiNameFromMethodAndPath(
        method,
        relativePath,
      );

      if (derivedApiName && derivedApiName !== apiName) {
        setApiName(derivedApiName);
      }
    }
  }, [method, relativePath]);

  const normalizeBodyType = (type) => {
    if (!type) return "NONE";

    if (type === "x-www-form-urlencoded") return "X_WWW_FORM_URLENCODED";
    if (type === "form-data") return "FORM_DATA";
    if (type === "raw") return "RAW";

    return type.toUpperCase();
  };

  useEffect(() => {
    if (!initialData) return;

    const rb = initialData.requestBody;

    if (!rb) {
      setBodyType("NONE");
      setRawJson("");
      setFormFields([]);
      return;
    }

    const mode = normalizeBodyType(rb.bodyType);
    setBodyType(mode);

    if (mode === "RAW") {
      setRawJson(rb.rawBody || "");
    }

    if (mode === "FORM_DATA" || mode === "X_WWW_FORM_URLENCODED") {
      setFormFields(rb.formFields || initialData.formFields || []);
    }
  }, [initialData]);

  useEffect(() => {
    setShowPathParams(relativePath.includes("{"));
  }, [relativePath]);

  // AUTO-CREATE PATH PARAMS FROM URL (Postman-like)
  useEffect(() => {
    if (!relativePath) return;

    // Only process if query exists
    if (!relativePath.includes("?")) return;

    const [pathPart, queryString] = relativePath.split("?");

    if (!queryString) return;

    const pairs = queryString.split("&");

    const queryParamsFromUrl = pairs
      .map((pair) => {
        const [key, value] = pair.split("=");

        if (!key) return null;

        const detectedType = detectTypeFromValue(value);

        return {
          name: key,
          paramIn: "query",
          type: detectedType,
          required: false,
          example: value || "",
        };
      })
      .filter(Boolean);

    if (queryParamsFromUrl.length === 0) return;

    // Add params only once
    setParams((prev) => {
      const existingQueryNames = prev
        .filter((p) => p.paramIn === "query")
        .map((p) => p.name);

      const newParams = queryParamsFromUrl.filter(
        (p) => !existingQueryNames.includes(p.name),
      );

      return [...prev, ...newParams];
    });
  }, [relativePath]);

  const formFieldsToBulkText = () =>
    formFields
      .filter((f) => f.enabled && f.key && f.fieldType !== "file")
      .map((f) => `${f.key}=${f.value ?? ""}`)
      .join("\n");

  const bulkTextToFormFields = (text) =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        let key, value;

        if (line.includes("=")) {
          [key, value] = line.split("=");
        } else {
          [key, value] = line.split(":");
        }

        return {
          enabled: true,
          key: key?.trim() || "",
          value: value?.trim() || "",
          description: "",
          fieldType: "text",
          filePath: "",
          fileName: "",
        };
      });

  useEffect(() => {
    if (!initialData && !projectId) {
      fetchProjects();
    } else if (projectId) {
      fetchProjectDetails(projectId);
    }
  }, [initialData, projectId]);

  useEffect(() => {
    setShowPathParams(relativePath.includes("{"));
  }, [relativePath]);

  useEffect(() => {
    if (bodyType === "X_WWW_FORM_URLENCODED") {
      setHeaders((prev) => {
        if (prev.some((h) => h.name === "Content-Type")) return prev;
        return [
          ...prev,
          {
            name: "Content-Type",
            value: "application/x-www-form-urlencoded",
          },
        ];
      });
    }
  }, [bodyType]);

  useEffect(() => {
    if (organizationId && projectId) {
      ProjectService.getProjectById(organizationId, projectId)
        .then((res) => {
          setProjectLocalBasePath(res.data.localBasePath || "");
          setProjectServerBasePath(res.data.serverBasePath || "");
          setProjectStageBasePath(res.data.stageBasePath || "");
        })
        .catch(() => {
          console.error("Failed to load project details");
        });
    }
  }, [organizationId, projectId]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await ProjectService.getAllProjects();

      const projectsData = Array.isArray(response?.data)
        ? response.data
        : response?.data
          ? [response.data]
          : [];

      setProjects(projectsData);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setErrors((prev) => ({ ...prev, projects: "Failed to load projects" }));
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    if (!organizationId || !projectId) return;

    try {
      const response = await ProjectService.getProjectById(
        organizationId,
        projectId,
      );

      setBasePath(response.data.basePath);

      if (response.data.modules && Array.isArray(response.data.modules)) {
        setModuleNameSuggestions(response.data.modules);
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
      setErrors((prev) => ({
        ...prev,
        projectDetails: "Failed to load project details",
      }));
    }
  };

  const handleProjectChange = async (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);

    //  SAFETY GUARD
    if (!organizationId || !projectId) {
      setBasePath("");
      setProjectLocalBasePath("");
      setProjectServerBasePath("");
      return;
    }

    try {
      const response = await ProjectService.getProjectById(
        organizationId,
        projectId,
      );

      setBasePath(response.data.basePath);
      setProjectLocalBasePath(response.data.localBasePath || "");
      setProjectServerBasePath(response.data.serverBasePath || "");
      setProjectStageBasePath(response.data.stageBasePath || "");
    } catch (error) {
      console.error("Error fetching project details:", error);
      setErrors((prev) => ({
        ...prev,
        projectDetails: "Failed to load project details",
      }));
    }
  };

  const addField = (parentIndex = null) => {
    const newField = {
      name: "",
      type: "string",
      example: "",
      required: false,
      children: [],
    };

    if (parentIndex === null) {
      setFields([...fields, newField]);
    } else {
      const newFields = [...fields];
      const addToChildren = (fields, indexPath) => {
        const [head, ...rest] = indexPath;
        if (rest.length === 0) {
          fields[head].children = [...(fields[head].children || []), newField];
        } else {
          addToChildren(fields[head].children, rest);
        }
      };
      addToChildren(newFields, parentIndex);
      setFields(newFields);
    }
  };

  const updateField = (indexPath, key, value) => {
    const updateNestedField = (fields, path) => {
      const [currentIndex, ...rest] = path;
      if (rest.length === 0) {
        fields[currentIndex][key] = value;

        // if type changed to object or array, make sure children exists
        if (key === "type") {
          if (["object", "array"].includes(value)) {
            if (!fields[currentIndex].children) {
              fields[currentIndex].children = [];
            }
          } else {
            // delete fields[currentIndex].children;
            fields[currentIndex].children = [];
          }
        }
      } else {
        updateNestedField(fields[currentIndex].children, rest);
      }
    };

    const newFields = [...fields];
    updateNestedField(newFields, indexPath);
    setFields(newFields);
  };

  const removeField = (indexPath) => {
    const removeNestedField = (fields, path) => {
      const [currentIndex, ...rest] = path;
      if (rest.length === 0) {
        fields.splice(currentIndex, 1);
      } else {
        removeNestedField(fields[currentIndex].children, rest);
      }
    };

    const newFields = [...fields];
    removeNestedField(newFields, indexPath);
    setFields(newFields);
  };

  // --- Params Handlers ---
  const addParam = () => {
    setParams([
      ...params,
      {
        name: "",
        paramIn: "query",
        type: "string",
        required: false,
        example: "",
        enabled: true, // ✅ NEW
      },
    ]);
  };

  useEffect(() => {
    setParams((prev) =>
      prev.map((p) => ({
        enabled: p.enabled !== false,
        ...p,
      })),
    );
  }, []);

  const updateParam = (index, key, value) => {
    const newParams = [...params];
    newParams[index][key] = value;
    setParams(newParams);
  };

  const removeParam = (index) => {
    setParams(params.filter((_, i) => i !== index));
  };

  // --- Headers Handlers ---
  const addHeader = () => setHeaders([...headers, { name: "", value: "" }]);
  const updateHeader = (index, key, value) => {
    const newHeaders = [...headers];
    newHeaders[index][key] = value;
    setHeaders(newHeaders);
  };
  const removeHeader = (index) =>
    setHeaders(headers.filter((_, i) => i !== index));

  const handleMethodChange = (e) => {
    const newMethod = e.target.value;
    setMethod(newMethod);

    // Clear fields when switching to methods that don't typically use request body
    if (["GET", "DELETE"].includes(newMethod)) {
      setFields([]);
    }

    // Set default active section based on method
    if (["POST", "PUT", "PATCH"].includes(newMethod)) {
      setActiveSection("fields");
    } else {
      setActiveSection("parameters");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const cleanFieldStructure = (fields) => {
      return fields.map((f) => {
        const cleaned = {
          name: f.name,
          type: f.type,
          example: f.example,
          required: f.required,
        };
        if (["object", "array"].includes(f.type)) {
          cleaned.children = cleanFieldStructure(f.children || []);
        }
        return cleaned;
      });
    };

    const cleanedFields = cleanFieldStructure(fields).filter(
      (f) => f.name.trim() !== "",
    );
    const cleanedParams = params.filter((p) => p.name.trim() !== "");
    const cleanedHeaders = headers.filter((h) => h.name.trim() !== "");

    //CLEAN FORM FIELDS (IMPORTANT)
    const cleanedFormFields = formFields.filter((f) => {
      if (f.enabled === false) return false;
      if (!f.key?.trim()) return false;

      if (bodyType === "FORM_DATA") {
        if (f.fieldType === "file") {
          return f.filePath && f.fileName;
        }
        return f.value && f.value.trim() !== "";
      }

      if (bodyType === "X_WWW_FORM_URLENCODED") {
        return f.value && f.value.trim() !== "";
      }

      return false;
    });

    console.log("FORM FIELDS:", formFields);
    console.log("CLEANED FORM FIELDS:", cleanedFormFields);

    const pathParamsInEndpoint = (relativePath.match(/{([^}]+)}/g) || []).map(
      (p) => p.slice(1, -1),
    );
    const pathParamsDefined = cleanedParams
      .filter((p) => p.paramIn === "path")
      .map((p) => p.name);

    const newErrors = {};

    if (
      (bodyType === "FORM_DATA" || bodyType === "X_WWW_FORM_URLENCODED") &&
      cleanedFormFields.length === 0
    ) {
      newErrors.fields = "Please add at least one valid form field";
    }

    if (!selectedProject) {
      newErrors.project = "Please select a project for this API";
    }

    if (!selectedModule?.id && !moduleName.trim()) {
      newErrors.moduleName = "Please select or enter a module name";
    } else if (!/^[A-Z]/.test(moduleName.trim())) {
      newErrors.moduleName = "Module name must start with a capital letter";
    }

    if (!apiName.trim()) {
      newErrors.apiName = "API name is required";
    } else if (!/^[A-Z]/.test(apiName.trim())) {
      newErrors.apiName = "API name must start with a capital letter";
    }

    if (!relativePath.startsWith("/")) {
      newErrors.relativePath =
        "Relative path must start with a forward slash (/)";
    }
    if (relativePath.includes("{{") || relativePath.includes("}}")) {
      newErrors.relativePath =
        "Invalid parameter format: use {param} instead of {{param}}";
    }

    const missingPathParams = pathParamsInEndpoint.filter(
      (p) => !pathParamsDefined.includes(p),
    );
    if (missingPathParams.length > 0) {
      newErrors.pathParams = `Missing path parameters: ${missingPathParams.join(
        ", ",
      )}`;
    }

    const extraPathParams = pathParamsDefined.filter(
      (p) => !pathParamsInEndpoint.includes(p),
    );
    if (extraPathParams.length > 0) {
      newErrors.pathParams = `Extra path parameters: ${extraPathParams.join(
        ", ",
      )}`;
    }

    if (bodyType === "RAW") {
      if (!rawJson.trim()) {
        newErrors.fields = "Raw JSON body is empty.";
      } else {
        try {
          JSON.parse(rawJson);
        } catch {
          newErrors.fields = "Invalid JSON format in Raw Body.";
        }
      }
    }

    if (
      showRequestBody &&
      schemaName.trim() &&
      !/^[A-Z]/.test(schemaName.trim())
    ) {
      newErrors.schemaName = "Schema name must start with a capital letter";
    }

    const collectFieldNames = (fields, names = []) => {
      fields.forEach((f) => {
        if (f.name) names.push(f.name.trim().toLowerCase());
        if (f.children) collectFieldNames(f.children, names);
      });
      return names;
    };

    const fieldNames = collectFieldNames(cleanedFields);
    const hasDuplicateFields = new Set(fieldNames).size !== fieldNames.length;
    if (hasDuplicateFields) {
      newErrors.fields =
        "Duplicate field names detected in Body fields (including nested fields)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    let finalFields = [];

    if (rawJson.trim()) {
      try {
        const parsed = JSON.parse(rawJson);
        finalFields = Object.keys(parsed).map((key) => ({
          name: key,
          type: typeof parsed[key],
          example: parsed[key],
          required: true,
        }));
      } catch (e) {
        setErrors({ fields: "Invalid JSON format in Raw Body." });
        setIsSubmitting(false);
        return;
      }
    }

    const payload = {
      apiName: apiName.trim(),
      schemaName: schemaName?.trim() || null,

      relativePath,
      method,
      summary,
      params: cleanedParams,
      headers: cleanedHeaders,
      auth,
      project: { id: Number(selectedProject) },
      module: selectedModule
        ? { id: selectedModule.id }
        : { name: moduleName.trim() },

      bodyInputMode: bodyType === "NONE" ? null : bodyType,

      rawJson: bodyType === "RAW" ? rawJson : null,

      formFields:
        bodyType === "FORM_DATA" || bodyType === "X_WWW_FORM_URLENCODED"
          ? cleanedFormFields
          : null,
    };

    try {
      if (typeof onSubmit === "function") {
        await onSubmit(payload);

        setErrors({});

        if (selectedProject) {
          await fetchProjectDetails(selectedProject);
        }
      } else {
        console.error("onSubmit is not a function");
        setErrors({ submit: "Form submission handler is not available" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Failed to submit API. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClick = () => {
    if (typeof onCancel === "function") {
      onCancel();
    } else {
      console.warn("onCancel is not a function");
    }
  };

  const addFormField = () => {
    setFormFields([
      ...formFields,
      {
        enabled: true,
        key: "",
        value: "",
        description: "",
        fieldType: "text",
        filePath: "",
        fileName: "",
      },
    ]);
  };

  const handleFileUpload = async (e, fieldIndex) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append("file", file);

    try {
      const res = await axios.post(
        "http://localhost:2011/api/files/upload",
        formDataObj,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { filePath, fileName } = res.data;

      setFormFields((prev) => {
        const updated = [...prev];
        updated[fieldIndex] = {
          ...updated[fieldIndex],
          filePath,
          fileName,
          fieldType: "file",
        };
        return updated;
      });
    } catch (err) {
      console.error("File upload failed", err);
    }
  };

  const updateFormField = (index, key, value) => {
    const updated = [...formFields];
    updated[index][key] = value;
    setFormFields(updated);
  };

  const removeFormField = (index) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "headers":
        return (
          <div className="section-content">
            <h3>Headers</h3>
            {headers.length === 0 && <p>No headers defined yet.</p>}
            {headers.map((header, index) => (
              <div key={index} className="header-container">
                <label>Name:</label>
                <input
                  type="text"
                  value={header.name}
                  onChange={(e) => updateHeader(index, "name", e.target.value)}
                  list="headerNameSuggestions"
                />
                <datalist id="headerNameSuggestions">
                  {headerNameSuggestions.map((s, i) => (
                    <option key={i} value={s} />
                  ))}
                </datalist>

                <label>Value:</label>
                <input
                  type="text"
                  value={header.value}
                  onChange={(e) => updateHeader(index, "value", e.target.value)}
                  list="headerValueSuggestions"
                />
                <datalist id="headerValueSuggestions">
                  {headerValueSuggestions.map((s, i) => (
                    <option key={i} value={s} />
                  ))}
                </datalist>

                <button type="button" onClick={() => removeHeader(index)}>
                  Remove Header
                </button>
              </div>
            ))}
            <button type="button" onClick={addHeader}>
              Add Header
            </button>
          </div>
        );

      case "authentication":
        return (
          <div className="section-content">
            <h3>Authentication</h3>
            <label>Auth Type:</label>
            <select
              value={auth.type}
              onChange={(e) => setAuth({ ...auth, type: e.target.value })}
            >
              <option value="none">None</option>
              <option value="basic">Basic Auth</option>
              <option value="bearer">Bearer Token</option>
              <option value="apikey">API Key</option>
            </select>

            {auth.type === "basic" && (
              <>
                <label>Username:</label>
                <input
                  type="text"
                  value={auth.username}
                  onChange={(e) =>
                    setAuth({ ...auth, username: e.target.value })
                  }
                />
                <label>Password:</label>
                <input
                  type="password"
                  value={auth.password}
                  onChange={(e) =>
                    setAuth({ ...auth, password: e.target.value })
                  }
                />
              </>
            )}

            {auth.type === "bearer" && (
              <>
                <label>Token:</label>
                <input
                  type="text"
                  value={auth.token}
                  onChange={(e) => setAuth({ ...auth, token: e.target.value })}
                />
              </>
            )}

            {auth.type === "apikey" && (
              <>
                <label>API Key Name:</label>
                <input
                  type="text"
                  value={auth.apiKeyName}
                  onChange={(e) =>
                    setAuth({ ...auth, apiKeyName: e.target.value })
                  }
                />
                <label>API Key Value:</label>
                <input
                  type="text"
                  value={auth.apiKeyValue}
                  onChange={(e) =>
                    setAuth({ ...auth, apiKeyValue: e.target.value })
                  }
                />
              </>
            )}
          </div>
        );

      case "parameters":
        return (
          <div className="section-content">
            <h3>Parameters</h3>
            {errors.pathParams && (
              <div className="error-message">{errors.pathParams}</div>
            )}

            {/* Path Parameters */}
            {showPathParams && (
              <>
                <h4>Path Parameters</h4>
                {params.filter((p) => p.paramIn === "path").length === 0 && (
                  <p>No path parameters defined yet.</p>
                )}

                {params
                  .map((param, index) => ({ param, index }))
                  .filter(({ param }) => param.paramIn === "path")
                  .map(({ param, index }) => (
                    <div key={index} className="param-container">
                      <label>Name:</label>
                      <input
                        type="text"
                        value={param.name}
                        onChange={(e) =>
                          updateParam(index, "name", e.target.value)
                        }
                        list="paramNameSuggestions"
                      />

                      <label>Location:</label>
                      <select value={param.paramIn} disabled>
                        <option value="path">path</option>
                      </select>

                      <label>Type:</label>
                      <select
                        value={param.type}
                        onChange={(e) =>
                          updateParam(index, "type", e.target.value)
                        }
                      >
                        {Object.entries(fieldTypeOptions).map(
                          ([group, types]) => (
                            <optgroup key={group} label={group}>
                              {types.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </optgroup>
                          ),
                        )}
                      </select>

                      <label>Required:</label>
                      <input type="checkbox" checked readOnly />
                    </div>
                  ))}
              </>
            )}

            <div className="param-tabs">
              <button
                type="button"
                className={paramTab === "query" ? "active" : ""}
                onClick={() => setParamTab("query")}
              >
                Query Params
              </button>

              <button
                type="button"
                className={paramTab === "path" ? "active" : ""}
                onClick={() => setParamTab("path")}
              >
                Path Params
              </button>

              <button
                type="button"
                className={paramTab === "header" ? "active" : ""}
                onClick={() => setParamTab("header")}
              >
                Header Params
              </button>
            </div>

            {/* All Parameters */}
            <h4>All Parameters</h4>

            {params.filter((p) => p.paramIn === paramTab).length === 0 && (
              <p>No parameters defined yet.</p>
            )}
            {params.filter((p) => p.paramIn === paramTab).length > 0 && (
              <table className="pm-param-table">
                <thead>
                  <tr>
                    <th>✓</th>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Example</th>
                    <th>Required</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {params
                    .map((param, index) => ({ param, index }))
                    .filter(({ param }) => param.paramIn === paramTab)
                    .map(({ param, index }) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="checkbox"
                            checked={param.enabled !== false}
                            onChange={(e) =>
                              updateParam(index, "enabled", e.target.checked)
                            }
                          />
                        </td>
                        <td>
                          <select
                            value={param.paramIn}
                            onChange={(e) =>
                              updateParam(index, "paramIn", e.target.value)
                            }
                            disabled={param.paramIn === "path"}
                          >
                            {paramInOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <select
                            value={param.type}
                            onChange={(e) =>
                              updateParam(index, "type", e.target.value)
                            }
                          >
                            {Object.entries(fieldTypeOptions).map(
                              ([group, types]) => (
                                <optgroup key={group} label={group}>
                                  {types.map((type) => (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </optgroup>
                              ),
                            )}
                          </select>
                        </td>

                        <td>
                          <input
                            value={param.example}
                            onChange={(e) =>
                              updateParam(index, "example", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={param.required}
                            onChange={(e) =>
                              updateParam(index, "required", e.target.checked)
                            }
                          />
                        </td>

                        <td>
                          <button
                            type="button"
                            disabled={param.paramIn === "path"}
                            onClick={() => removeParam(index)}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            {/* ✅ THIS IS THE CORRECT PLACE */}
            <button type="button" onClick={addParam}>
              + Add Parameter
            </button>
          </div>
        );

      case "fields":
        return showRequestBody ? (
          <div className="section-content">
            <h3>Request Body</h3>

            <label>Body Type:</label>
            <select
              value={bodyType}
              onChange={(e) => setBodyType(e.target.value)}
            >
              <option value="NONE">None</option>
              <option value="RAW">Raw (JSON)</option>
              <option value="FORM_DATA">Form Data</option>
              <option value="X_WWW_FORM_URLENCODED">
                x-www-form-urlencoded
              </option>
            </select>

            {bodyType === "NONE" && (
              <div className="info-message">
                This request does not have a body
              </div>
            )}

            {bodyType === "RAW" && (
              <>
                <h4>Raw JSON:</h4>
                <CodeMirror
                  value={rawJson}
                  height="300px"
                  theme="light"
                  extensions={[json()]}
                  onChange={(value) => setRawJson(value)}
                />
              </>
            )}

            {/* Schema Name */}
            <label>Schema Name:</label>
            <input
              type="text"
              value={schemaName}
              onChange={(e) => {
                setSchemaName(e.target.value);
                setIsSchemaManuallyEdited(true);
              }}
              placeholder="Book"
              list="schemaNameSuggestions"
              required
            />

            <datalist id="schemaNameSuggestions">
              {schemaNameSuggestions.map((s, i) => (
                <option key={i} value={s} />
              ))}
            </datalist>

            {errors.schemaName && (
              <div className="error-message">{errors.schemaName}</div>
            )}

            {(bodyType === "FORM_DATA" ||
              bodyType === "X_WWW_FORM_URLENCODED") && (
              <>
                <h4>Form Fields</h4>

                {/* 🔁 Postman-style Edit Mode Toggle */}
                <div
                  className="form-edit-toggle"
                  style={{ marginBottom: "10px" }}
                >
                  <button
                    type="button"
                    className={formEditMode === "KEY_VALUE" ? "active" : ""}
                    onClick={() => setFormEditMode("KEY_VALUE")}
                  >
                    Key-Value Edit
                  </button>

                  <button
                    type="button"
                    className={formEditMode === "BULK" ? "active" : ""}
                    onClick={() => setFormEditMode("BULK")}
                  >
                    Bulk Edit
                  </button>
                </div>

                {errors.fields && (
                  <div className="error-message">{errors.fields}</div>
                )}

                {formEditMode === "KEY_VALUE" && (
                  <>
                    <table className="pm-form-table">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Key</th>
                          <th>{bodyType === "FORM_DATA" ? "Type" : "-"}</th>
                          <th>Value</th>
                          <th>Description</th>
                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {formFields.map((f, i) => (
                          <tr key={i}>
                            <td>
                              <input
                                type="checkbox"
                                checked={f.enabled}
                                onChange={(e) =>
                                  updateFormField(
                                    i,
                                    "enabled",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                value={f.key}
                                placeholder="key"
                                onChange={(e) =>
                                  updateFormField(i, "key", e.target.value)
                                }
                              />
                            </td>

                            <td>
                              {bodyType === "FORM_DATA" ? (
                                <select
                                  value={f.fieldType}
                                  disabled={formEditMode === "BULK"}
                                  onChange={(e) =>
                                    updateFormField(
                                      i,
                                      "fieldType",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="text">Text</option>
                                  <option value="file">File</option>
                                </select>
                              ) : (
                                "-"
                              )}
                            </td>

                            <td>
                              {bodyType === "FORM_DATA" &&
                              f.fieldType === "file" ? (
                                <>
                                  {f.fileName ? (
                                    <div className="saved-file">
                                      📎 {f.fileName}
                                      <button
                                        type="button"
                                        style={{ marginLeft: "8px" }}
                                        onClick={() => {
                                          updateFormField(i, "filePath", "");
                                          updateFormField(i, "fileName", "");
                                        }}
                                      >
                                        Change
                                      </button>
                                    </div>
                                  ) : (
                                    <input
                                      type="file"
                                      disabled={formEditMode === "BULK"}
                                      onChange={(e) => handleFileUpload(e, i)}
                                    />
                                  )}
                                </>
                              ) : (
                                <input
                                  value={f.value}
                                  placeholder="value"
                                  onChange={(e) =>
                                    updateFormField(i, "value", e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td>
                              <input
                                value={f.description}
                                placeholder="description"
                                onChange={(e) =>
                                  updateFormField(
                                    i,
                                    "description",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td>
                              <button
                                type="button"
                                onClick={() => removeFormField(i)}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <button type="button" onClick={addFormField}>
                      + Add Field
                    </button>
                  </>
                )}

                {formEditMode === "BULK" && (
                  <textarea
                    className="bulk-editor"
                    rows={10}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={`username=admin\npassword=1234`}
                    style={{ width: "100%" }}
                  />
                )}
              </>
            )}
          </div>
        ) : (
          <div className="info-message">
            <p>Request body is not typically used with {method} requests.</p>
          </div>
        );

      case "preview":
        return (
          <div className="section-content">
            <h3>Request Preview</h3>

            {/* METHOD + URL */}
            <pre>{`${method} ${buildFullUrlWithQuery()}`}</pre>

            {/* HEADERS */}
            {headers.length > 0 && (
              <>
                <h4>Headers</h4>
                <pre>
                  {headers
                    .filter((h) => h.name && h.value)
                    .map((h) => `${h.name}: ${h.value}`)
                    .join("\n")}
                </pre>
              </>
            )}

            {/* BODY */}
            {bodyType === "RAW" && rawJson && (
              <>
                <h4>Body (Raw JSON)</h4>
                <pre>{rawJson}</pre>
              </>
            )}

            {(bodyType === "FORM_DATA" ||
              bodyType === "X_WWW_FORM_URLENCODED") && (
              <>
                <h4>Body ({bodyType})</h4>
                <pre>
                  {formFields
                    .filter((f) => f.key)
                    .map((f) =>
                      f.fieldType === "file"
                        ? `${f.key}=[file]`
                        : `${f.key}=${f.value}`,
                    )
                    .join("\n")}
                </pre>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="api-builder-container">
      <h2>{initialData ? "Edit API" : "Create New API"}</h2>
      {errors.submit && <div className="error-message">{errors.submit}</div>}

      <form onSubmit={handleSubmit}>
        {/* Project Selection */}
        {!initialData && !projectId && (
          <>
            <h3>Project Selection</h3>
            <label>Select Project:</label>
            {loadingProjects ? (
              <div>Loading projects...</div>
            ) : (
              <>
                <select
                  value={selectedProject}
                  onChange={handleProjectChange}
                  required
                  className={errors.project ? "error-field" : ""}
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.basePath})
                    </option>
                  ))}
                </select>
                {errors.project && (
                  <div className="error-message">{errors.project}</div>
                )}
              </>
            )}
            {errors.projects && (
              <div className="error-message">{errors.projects}</div>
            )}
          </>
        )}

        {/* Local + Server Base Path Display */}
        {(selectedProject || projectId) && (
          <>
            <h4>Local Base Path</h4>
            <input
              type="text"
              value={projectLocalBasePath || ""}
              readOnly
              className="read-only-input"
            />

            <h4>Production Base Path</h4>
            <input
              type="text"
              value={projectServerBasePath || ""}
              readOnly
              className="read-only-input"
            />

            <h4>Stage Base Path</h4>
            <input
              type="text"
              value={projectStageBasePath || ""}
              readOnly
              className="read-only-input"
            />
          </>
        )}

        {/* --- Endpoint Details --- */}
        <h3>Endpoint Details</h3>

        {/* Module Name Input */}
        <label>Module Name:</label>
        <div className="module-select-container">
          <select
            value={selectedModule?.id || ""}
            onChange={(e) => {
              const module = moduleNameSuggestions.find(
                (m) => String(m.id) === e.target.value,
              );
              setSelectedModule(module);
              setModuleName(module.name); // optional, for display
            }}
          >
            <option value="">-- Select existing module --</option>
            {moduleNameSuggestions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <span style={{ margin: "0 10px" }}>or</span>

          <input
            type="text"
            value={moduleName}
            onChange={(e) => {
              setModuleName(e.target.value);
              setSelectedModule(null);
            }}
            placeholder="Enter new module name"
          />
        </div>

        {errors.moduleName && (
          <div className="error-message">{errors.moduleName}</div>
        )}

        <label>Relative Path:</label>
        <input
          type="text"
          value={relativePath}
          onChange={(e) => setRelativePath(e.target.value)}
          list="endpointSuggestions"
          placeholder="/books/{id}"
          required
          className={errors.relativePath ? "error-field" : ""}
        />

        {/* API Name */}
        <label>API Name:</label>
        <input
          type="text"
          value={apiName}
          onChange={(e) => {
            setApiName(e.target.value);
            setIsApiNameManuallyEdited(true);
          }}
          placeholder="GetBookById"
          className={errors.apiName ? "error-field" : ""}
        />

        {errors.apiName && (
          <div className="error-message">{errors.apiName}</div>
        )}

        <datalist id="endpointSuggestions">
          {endpointSuggestions.map((s, i) => (
            <option key={i} value={s} />
          ))}
        </datalist>
        {errors.relativePath && (
          <div className="error-message">{errors.relativePath}</div>
        )}

        <div className="full-path-preview">
          <label>Full Path Preview (Local):</label>
          <input
            type="text"
            value={`${projectLocalBasePath || ""}${relativePath}`}
            readOnly
            className="read-only-input"
          />

          <label>Full Path Preview (Production):</label>
          <input
            type="text"
            value={`${projectServerBasePath || ""}${relativePath}`}
            readOnly
            className="read-only-input"
          />
          <label>Full Path Preview (Stage):</label>
          <input
            type="text"
            value={`${projectStageBasePath || ""}${relativePath}`}
            readOnly
            className="read-only-input"
          />
        </div>

        <label>HTTP Method:</label>
        <select value={method} onChange={handleMethodChange} required>
          {methodOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <label>Summary:</label>
        <input
          type="text"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Create a new book"
          required
        />

        {/* Section Navigation Buttons */}
        <div className="section-buttons">
          <button
            type="button"
            className={`section-btn ${
              activeSection === "parameters" ? "active" : ""
            }`}
            onClick={() => setActiveSection("parameters")}
          >
            Parameters
          </button>
          <button
            type="button"
            className={`section-btn ${
              activeSection === "headers" ? "active" : ""
            }`}
            onClick={() => setActiveSection("headers")}
          >
            Headers
          </button>
          <button
            type="button"
            className={`section-btn ${
              activeSection === "authentication" ? "active" : ""
            }`}
            onClick={() => setActiveSection("authentication")}
          >
            Authentication
          </button>
          <button
            type="button"
            className={`section-btn ${
              activeSection === "fields" ? "active" : ""
            }`}
            onClick={() => setActiveSection("fields")}
            disabled={!showRequestBody}
            title={
              !showRequestBody
                ? `Request body not typically used with ${method} requests`
                : ""
            }
          >
            Request Body
          </button>

          <button
            type="button"
            className={`section-btn ${
              activeSection === "preview" ? "active" : ""
            }`}
            onClick={() => setActiveSection("preview")}
          >
            Preview
          </button>
        </div>

        {renderSectionContent()}

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting
              ? "Submitting..."
              : initialData
                ? "Update API"
                : "Create API"}
          </button>
          <button
            type="button"
            onClick={handleCancelClick}
            className="cancel-btn"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApiBuilderForm;
